import * as React from "react";
import NextImage from "next/image";
import { Heading, Text, Box, Stack } from "@chakra-ui/react";

import Container from "components/Chakra/Container";
import ContactBadge from "components/ContactBadge";
import IconList from "components/IconList";
import Logos from "components/Logos";
import MetaHead from "components/MetaHead";
import CityHeader from "components/CityHeader";
import useViewport from "hooks/useViewport";
import Card from "components/Chakra/Card";
import Button from "components/Chakra/Button";
import CustomLink from "components/Chakra/CustomLink";
import CStack from "components/Chakra/ResponsiveStack";

const LOGOS = [
  {
    url: "https://www.bravis.cz/",
    src: "/img/common/logos/bravis.png",
  },
  {
    url: "https://www.nextreality.cz/",
    src: "/img/common/logos/next.png",
  },
  {
    url: "https://www.century21.cz/",
    src: "/img/common/logos/century-21.png",
  },
  {
    url: "https://www.fincentrumreality.com/",
    src: "/img/common/logos/swisslife.png",
  },
  {
    url: "https://www.realspektrum.cz/",
    src: "/img/common/logos/real-spektrum.png",
  },
  {
    url: "https://qara.cz/",
    src: "/img/common/logos/qara.png",
  },
  {
    url: "https://www.justo.cz/",
    src: "/img/common/logos/justo.png",
  },
  {
    url: "https://www.soccer-reality.cz/",
    src: "/img/common/logos/soccer.png",
  },
];

const RealEstateOffer: React.FC = () => {
  const { isMobile } = useViewport();

  return (
    <>
      <MetaHead
        title="Výhodné balíčky prověření pro realitní specialisty"
        description="Chraňte své klienty – v Česku má každý 10. alespoň jednu exekuci. Prověřením záznamů o zájemcích ušetříte klientům starosti s rizikovými nájemníky."
      />

      <CityHeader>
        <Box>
          <Heading as="h1" mb="40px">
            Chraňte své klienty
          </Heading>
          <Text textAlign="center" variant="large_2" mb="60px">
            Věděli jste, že{" "}
            <Text
              display="inline"
              variant="large_2"
              fontWeight="bold"
              as="span"
            >
              v Česku má každý 10. alespoň jednu exekuci?{" "}
            </Text>
            {!isMobile && <br />}
            Prověřením záznamů o zájemcích ušetříte klientům starosti
            s rizikovými nájemníky.
          </Text>
          <Box textAlign="center">
            <Button
              onClick={() => {
                const top = document.getElementById("balicky")?.offsetTop || 0;
                window.scrollTo({
                  behavior: "smooth",
                  top: top - 100,
                });
              }}
            >
              Výhodné balíčky prověření
            </Button>
          </Box>
        </Box>
      </CityHeader>
      <Logos priority brands={LOGOS} />
      <Container>
        <Heading as="h2" my="5rem" fontWeight="light" textAlign="center">
          Prověřte bezdlužnost zájemců o pronájem
        </Heading>
        <CStack
          spacing="20px"
          direction={{ base: "column", md: "row" }}
          justify="center"
        >
          <IconList
            image="/img/icons/zajemci.svg"
            title="Včas odhalíte možná rizika"
            body="Prověřením bonity ušetříte sobě a klientům peníze i čas s případným problémovým nájemcem. Zároveň tím ochráníte dobré jméno své kanceláře a klientům přinesete jen ty nejkvalitnější nájemníky."
            align="left"
            customWidth={{ base: "100%", md: "265px" }}
          />
          <IconList
            image="/img/icons/smlouvy.svg"
            title="Nájemce prověříte instantně"
            body="Stačí zadat celé jméno a datum narození, a než se nadějete, máte v rukou přehledný report s logem vaší realitní kanceláře. Zároveň získáte certifikát prověření, který můžete vystavit na webu či v kanceláři."
            align="left"
            customWidth={{ base: "100%", md: "265px" }}
          />
          <IconList
            image="/img/icons/provereni_zajemce.svg"
            title="Prověřujeme v nejdůležitějších rejstřících najednou"
            body="Údaje o osobě prověříme v českých nebo slovenských rejstřících. Prověřit krom exekuce a insolvence lze i trvalou adresu, zda neodpovídá adrese městských úřadů."
            align="left"
            customWidth={{ base: "100%", md: "265px" }}
          />
        </CStack>
        <Stack alignItems="center" mb="90px" mt="18px">
          <Stack justify="center" spacing={0} direction="row">
            <CustomLink
              trackEventPayload={{
                "dl.eCat": "CC",
                "dl.eAct": "cc_click",
                "dl.eLab": "balicky_provereni",
              }}
              to="/provereni-rizikovosti-osob"
              variant="button_outline"
            >
              Prověřit zájemce
            </CustomLink>
          </Stack>
        </Stack>
        <Stack
          direction={{ base: "column", md: "row" }}
          gap="56px"
          mt="60px"
          spacing={0}
          justifyContent="center"
        >
          <Stack
            spacing={3}
            alignItems="center"
            flex={1}
            justify="space-between"
          >
            <Text textAlign="center" variant="large_2">
              <Text variant="large_3" fontWeight="bold" as="span">
                27 000 +
              </Text>
              prověřených
              <br />
              osob
            </Text>
            <NextImage
              src="/img/icons/arrow_up.svg"
              alt=""
              width="78"
              height="39"
            />
          </Stack>
          <Stack
            spacing={3}
            alignItems="center"
            flex={1}
            justify="space-between"
          >
            <Text textAlign="center" variant="large_2">
              <Text variant="large_3" fontWeight="bold" as="span">
                29,5 %
              </Text>
              osob mělo aspoň <br />
              jeden záznam
            </Text>
            <NextImage
              src="/img/icons/arrow_up.svg"
              alt=""
              width="78"
              height="39"
            />
          </Stack>
          <Stack
            spacing={3}
            alignItems="center"
            flex={1}
            justify="space-between"
          >
            <Text textAlign="center" variant="large_2">
              <Text
                variant="large_3"
                fontWeight="bold"
                lineHeight="32px"
                as="span"
              >
                200 +
              </Text>
              realitních kanceláří
              <br />
              nám důvěřuje
            </Text>
            <NextImage
              src="/img/icons/arrow_up.svg"
              alt=""
              width="78"
              height="39"
            />
          </Stack>
        </Stack>
        <div id="balicky" />
        <Card styleProps={{ mt: "120px", mb: "90px" }}>
          <Text
            variant="large_2"
            fontWeight="bold"
            textAlign="center"
            p="18px 30px"
          >
            Výhodné balíčky prověření osob
          </Text>
          <Text textAlign="center" pb="48px">
            Oproti jednorázovému prověření (149 Kč vč. DPH)
            <Text fontWeight="bold" display="inline" as="span">
              {" "}
              ušetříte až 37 %{" "}
            </Text>
            – nebo i více s balíčkem na míru.
          </Text>

          <Stack
            direction={{ base: "column", md: "row" }}
            alignItems={{ base: "center", md: "unset" }}
            justify="space-around"
            spacing={1}
            mb="30px"
          >
            <Stack
              direction="column"
              justify="space-between"
              alignItems="center"
              spacing={0}
              maxW="300px"
            >
              <Card styleProps={{ w: "270px", p: "20px 15px" }}>
                <Stack
                  direction="row"
                  justify="space-between"
                  flexWrap="nowrap"
                  spacing={0}
                >
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Text fontWeight="bold">Balíček 25 ks</Text>
                  </Stack>
                  <Text color="ud-primary" fontWeight="bold" display="inline">
                    110 Kč
                    <Text
                      display="inline"
                      color="ud-primary"
                      fontWeight="normal"
                      as="span"
                    >
                      &#47;os
                    </Text>
                  </Text>
                </Stack>
              </Card>
              <Card styleProps={{ w: "270px", p: "20px 15px" }}>
                <Stack
                  direction="row"
                  justify="space-between"
                  flexWrap="nowrap"
                  spacing={0}
                >
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Text fontWeight="bold">Balíček 50 ks</Text>
                  </Stack>
                  <Text color="ud-primary" fontWeight="bold" display="inline">
                    100 Kč
                    <Text
                      display="inline"
                      color="ud-primary"
                      fontWeight="normal"
                      as="span"
                    >
                      &#47;os
                    </Text>
                  </Text>
                </Stack>
              </Card>
              <Card styleProps={{ w: "270px", p: "20px 15px" }}>
                <Stack
                  direction="row"
                  justify="space-between"
                  flexWrap="nowrap"
                  spacing={0}
                >
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Text fontWeight="bold">Balíček 100 ks</Text>
                  </Stack>
                  <Text color="ud-primary" fontWeight="bold" display="inline">
                    90 Kč
                    <Text
                      display="inline"
                      color="ud-primary"
                      fontWeight="normal"
                      as="span"
                    >
                      &#47;os
                    </Text>
                  </Text>
                </Stack>
              </Card>
              <Card styleProps={{ w: "270px", p: "20px 15px" }}>
                <Stack
                  direction="row"
                  justify="space-between"
                  flexWrap="nowrap"
                  spacing={0}
                >
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Text fontWeight="bold">
                      Balíček 100
                      <Text display="inline" color="ud-primary" as="span">
                        +
                      </Text>{" "}
                      ks
                    </Text>
                  </Stack>
                  <Text
                    color="ud-primary"
                    fontWeight="bold"
                    display="inline"
                    flex="none"
                  >
                    <Text
                      display="inline"
                      color="ud-primary"
                      fontWeight="bold"
                      as="span"
                    >
                      Cena na míru
                    </Text>
                  </Text>
                </Stack>
              </Card>
              <Text
                w="100%"
                variant="small_1"
                color="ud-primary"
                textAlign="right"
              >
                Ceny jsou bez DPH
              </Text>
            </Stack>
            <Stack
              direction="column"
              spacing={0}
              justify="space-around"
              alignItems="center"
            >
              <Stack spacing={0} mt="48px" mb="30px">
                <ContactBadge
                  imageUrl="/img/common/kolacek.jpg"
                  name="Petr Koláček"
                  title="Key Account Manager"
                  email="kolacek@ulovdomov.cz"
                  phone="+420 773 128 704"
                />
              </Stack>

              <CustomLink
                trackEventPayload={{
                  "dl.eCat": "RK",
                  "dl.eAct": "rk_materialy",
                  "dl.eLab": "vyhodna_nabidka_cc",
                }}
                href="/documents/balicky.pdf"
                isExternal
              >
                <CStack direction="row" alignItems="center" flexWrap="nowrap">
                  <Text color="ud-primary" textAlign="left">
                    Výhodná nabídka prověření
                  </Text>
                </CStack>
              </CustomLink>
            </Stack>
          </Stack>
        </Card>
      </Container>
    </>
  );
};

export default RealEstateOffer;
